export type TemplateSaveSettlement =
  | { status: 'saved' }
  | { status: 'cancelled' }
  | { status: 'blocked'; message: string }
  | { status: 'stale' }
  | { status: 'failed'; message: string }
  | { status: 'awaiting_confirmation' };

type SaveDialogResult = 'dialog' | 'blocked';

type PendingSave = {
  expectedWorkingRevision: number;
  resolve: (result: TemplateSaveSettlement) => void;
};

let openSave: (() => SaveDialogResult) | null = null;
let closeSave: (() => void) | null = null;
let confirmSave: (() => void) | null = null;
let pending: PendingSave | null = null;
let armedRevision: number | null = null;

export function registerTemplateSaveRequest(
  open: () => SaveDialogResult,
  close: () => void,
  confirm: () => void,
): () => void {
  openSave = open;
  closeSave = close;
  confirmSave = confirm;
  return () => {
    if (openSave === open) {
      openSave = null;
      closeSave = null;
      confirmSave = null;
      armedRevision = null;
      settleTemplateSave({ status: 'cancelled' });
    }
  };
}

export function getPendingTemplateSave(): { expectedWorkingRevision: number } | null {
  if (pending) {
    return { expectedWorkingRevision: pending.expectedWorkingRevision };
  }
  if (armedRevision != null) {
    return { expectedWorkingRevision: armedRevision };
  }
  return null;
}

export function settleTemplateSave(result: TemplateSaveSettlement): void {
  const current = pending;
  pending = null;
  armedRevision = null;
  current?.resolve(result);
}

export function requestTemplateSave(request: {
  expectedWorkingRevision: number;
  userConfirmed?: boolean;
  cancel?: boolean;
  signal?: AbortSignal;
}): Promise<TemplateSaveSettlement> {
  if (!openSave || !confirmSave) {
    return Promise.reject(new Error('Template Builder save is not available.'));
  }
  if (request.signal?.aborted) {
    return Promise.resolve({ status: 'cancelled' });
  }
  if (request.cancel) {
    closeSave?.();
    if (pending) {
      settleTemplateSave({ status: 'cancelled' });
    } else {
      armedRevision = null;
    }
    return Promise.resolve({ status: 'cancelled' });
  }
  if (pending) {
    return Promise.reject(new Error('A save confirmation is already open.'));
  }

  if (!request.userConfirmed) {
    let outcome: SaveDialogResult;
    try {
      outcome = openSave();
    } catch (error) {
      return Promise.reject(error);
    }
    if (outcome === 'blocked') {
      return waitForSettlement(request.expectedWorkingRevision, request.signal);
    }
    armedRevision = request.expectedWorkingRevision;
    return Promise.resolve({ status: 'awaiting_confirmation' });
  }

  return new Promise((resolve, reject) => {
    const finish = (result: TemplateSaveSettlement) => {
      request.signal?.removeEventListener('abort', onAbort);
      resolve(result);
    };
    const onAbort = () => {
      closeSave?.();
      settleTemplateSave({ status: 'cancelled' });
    };
    pending = {
      expectedWorkingRevision: request.expectedWorkingRevision,
      resolve: finish,
    };
    request.signal?.addEventListener('abort', onAbort, { once: true });
    try {
      const outcome = openSave?.();
      if (outcome === 'dialog') {
        confirmSave?.();
      }
    } catch (error) {
      pending = null;
      armedRevision = null;
      request.signal?.removeEventListener('abort', onAbort);
      reject(error);
    }
  });
}

function waitForSettlement(
  expectedWorkingRevision: number,
  signal?: AbortSignal,
): Promise<TemplateSaveSettlement> {
  return new Promise((resolve) => {
    const finish = (result: TemplateSaveSettlement) => {
      signal?.removeEventListener('abort', onAbort);
      resolve(result);
    };
    const onAbort = () => {
      closeSave?.();
      settleTemplateSave({ status: 'cancelled' });
    };
    pending = {
      expectedWorkingRevision,
      resolve: finish,
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}
